import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import type { WidgetConfig } from "./types";

interface WidgetWrapperProps {
  widget: WidgetConfig;
  isEditMode: boolean;
  showEditControls?: boolean;
  controlsMode?: "full" | "configure" | "none";
  isDragging?: boolean;
  dragListeners?: Record<string, unknown>;
  onRemove: (id: string) => void;
  onConfigure: (id: string) => void;
  children: React.ReactNode;
}

export function WidgetWrapper({
  widget,
  isEditMode,
  showEditControls = true,
  controlsMode = "full",
  isDragging,
  dragListeners,
  onRemove,
  onConfigure,
  children,
}: WidgetWrapperProps) {
  const [showConfirm, setShowConfirm] = useState(false);

  return (
    <div
      className={`h-full w-full relative group ${isEditMode ? "ring-1 ring-dashed ring-[var(--brand-primary)]/30 rounded-xl" : ""}`}
      style={{ opacity: isDragging ? 0 : 1 }}
    >
      {/* Drag handle (edit mode only) */}
      <AnimatePresence>
        {isEditMode && showEditControls && controlsMode !== "none" && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className={`absolute -top-0 left-0 right-0 z-20 flex items-center justify-between px-3 py-1.5 bg-background/80 backdrop-blur-sm rounded-t-xl border-b border-border/50 ${
              controlsMode === "full" ? "widget-drag-handle cursor-grab active:cursor-grabbing" : ""
            }`}
            {...(controlsMode === "full" ? (dragListeners as Record<string, React.EventHandler<React.SyntheticEvent>>) : {})}
          >
            <div className="flex items-center gap-2">
              {controlsMode === "full" ? (
                <svg className="w-4 h-4 text-muted-foreground" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M7 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM7 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM7 14a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 14a2 2 0 1 0 0 4 2 2 0 0 0 0-4z" />
                </svg>
              ) : (
                <svg className="w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M14 4l6 6-2.25.75-2.5 2.5L14.5 16l-3.75-3.75-4.5 4.5-1.75-1.75 4.5-4.5L5.25 6.75 8 6l2.5-2.5L11 1.25z" />
                </svg>
              )}
              <span className="text-xs font-medium text-muted-foreground truncate max-w-[200px]">
                {widget.title}
              </span>
            </div>
            <div className="flex items-center gap-1">
              {/* Settings button */}
              <button
                onClick={(e) => { e.stopPropagation(); onConfigure(widget.id); }}
                className="p-1 rounded hover:bg-muted transition-colors"
                title="Configure widget"
              >
                <svg className="w-3.5 h-3.5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </button>
              {controlsMode === "full" && (showConfirm ? (
                <div className="flex items-center gap-1">
                  <button
                    onClick={(e) => { e.stopPropagation(); onRemove(widget.id); setShowConfirm(false); }}
                    className="px-1.5 py-0.5 text-xs bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                  >
                    Yes
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowConfirm(false); }}
                    className="px-1.5 py-0.5 text-xs bg-muted text-muted-foreground rounded hover:bg-muted/80 transition-colors"
                  >
                    No
                  </button>
                </div>
              ) : (
                <button
                  onClick={(e) => { e.stopPropagation(); setShowConfirm(true); }}
                  className="p-1 rounded hover:bg-red-500/10 transition-colors"
                  title="Remove widget"
                  >
                    <svg className="w-3.5 h-3.5 text-muted-foreground hover:text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Widget content */}
      {isEditMode ? (
        <div className="absolute inset-0 overflow-hidden rounded-[var(--theme-card-radius,16px)]">
          <div className="h-full w-full [&>*]:h-full">
            {children}
          </div>
        </div>
      ) : (
        <div className="h-full w-full [&>*]:h-full">
          {children}
        </div>
      )}
    </div>
  );
}
